import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";

import { faInfoCircle, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { ButtonStandard, Pagination } from "@performyard/py-components";

import PageHeading from "../../../components/PageHeading";
import AllWorkflowsTable from "./AllWorkflowsTable";
import ReadableWorkflowsFilterChips from "./ReadableWorkflowsFilterChips";
import WorkflowsFilterPanel, { WorkflowsFilters } from "./WorkflowsFilterPanel";

import type { SortDirection } from "../../../api/hooks";
import { useRbacPrimaryRolePermissions, useWorkflows } from "../../../api/hooks";
import { useIsNewNavigation } from "../../../api/hooks/defs/navigation";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";
import useNavigate from "../../../hooks/useNavigate";

import type { WorkflowSortBy } from "../../../api/services/defs/workflows";

import { getCompanySettingsContext, settingsHaveBeenFetched } from "../../../selectors/settings";

import { appendCurrentLocationAsFrom } from "../../../utils/workflowsBreadcrumbs";

import InfoPageTemplate from "../../../pageTemplates/InfoPageTemplate";
import StandardToolbarActionRow from "../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import { WORKFLOWS_NAVIGATION_LINKS } from "../navigationLinks";

const PAGE_TITLE = "Company Workflows";
const PAGE_SIZE = 25;

// The sort params live in the URL, so they can hold arbitrary values (old bookmarks
// from the legacy page used `name`/`direction` for filters). Fall back to the
// defaults instead of sending an invalid sort the API would reject.
const sanitizeSortBy = (value: string): WorkflowSortBy => (value === "name" ? "name" : "start_date");
const sanitizeSortDirection = (value: string): SortDirection => (value === "asc" ? "asc" : "desc");

const OBJECT_ID_PATTERN = /^[0-9a-fA-F]{24}$/;
const MAX_NAME_FILTER_LENGTH = 256;

const normalizeWorkflowsFilters = (filters: Partial<WorkflowsFilters>): Partial<WorkflowsFilters> => {
	const normalized: Partial<WorkflowsFilters> = {};
	if (typeof filters.workflowName === "string" && filters.workflowName) {
		normalized.workflowName = filters.workflowName.slice(0, MAX_NAME_FILTER_LENGTH);
	}
	if (
		filters.subject &&
		typeof filters.subject === "object" &&
		typeof filters.subject.id === "string" &&
		typeof filters.subject.name === "string" &&
		OBJECT_ID_PATTERN.test(filters.subject.id)
	) {
		normalized.subject = { id: filters.subject.id, name: filters.subject.name };
	}
	if (filters.status === "active" || filters.status === "completed" || filters.status === "overdue") {
		normalized.status = filters.status;
	}
	if (Array.isArray(filters.overdueOnly) && filters.overdueOnly.length > 0) {
		normalized.overdueOnly = ["true"];
	}
	if (Array.isArray(filters.unassignedOnly) && filters.unassignedOnly.length > 0) {
		normalized.unassignedOnly = ["true"];
	}
	return normalized;
};

const AllWorkflows = () => {
	const isNewNav = useIsNewNavigation();
	const navigate = useNavigate();
	const [isFiltersOpen, setIsFiltersOpen] = useState(false);

	const {
		value: workflowsFeatureVisible,
		query: { isLoading: isLoadingWorkflowsFF },
	} = useFeatureFlagQuery("ff-is-workflows-enabled", true);
	const { data: rbacPermissions, isLoading: isRbacLoading } = useRbacPrimaryRolePermissions("workflows");

	const companySettings = useSelector(getCompanySettingsContext);
	const haveSettingsBeenFetched = useSelector(settingsHaveBeenFetched);
	const workflowsEnabled = companySettings.getIn(["workflows", "state"], "disabled") === "enabled";

	const {
		pageNumber,
		setPageNumber,
		sort,
		setSort,
		filters,
		setFilters,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
	} = useInfoPageTemplateQueryState<WorkflowsFilters>({
		defaultSortName: "start_date",
		defaultSortDirection: "desc",
		defaultPageNumber: 1,
		normalizeFilters: normalizeWorkflowsFilters,
	});

	const isGateLoading = isLoadingWorkflowsFF || isRbacLoading || !haveSettingsBeenFetched;
	const canBypassFeatureGate = rbacPermissions?.includes("workflows:bypassFeatureGate");
	const featureAvailable = canBypassFeatureGate || (workflowsFeatureVisible && workflowsEnabled);
	const hasViewPermission = rbacPermissions?.includes("workflows:viewAdmin");

	const { data, isLoading } = useWorkflows(
		{
			sortBy: sanitizeSortBy(sort.name),
			sortOrder: sanitizeSortDirection(sort.direction),
			limit: PAGE_SIZE,
			skip: (pageNumber - 1) * PAGE_SIZE,
			name: debouncedSearchValue?.slice(0, MAX_NAME_FILTER_LENGTH) || undefined,
			workflowName: filters.workflowName || undefined,
			subject: filters.subject?.id,
			status: filters.status && filters.status !== "any" ? filters.status : undefined,
			overdueOnly: (filters.overdueOnly?.length ?? 0) > 0 || undefined,
			unassignedOnly: (filters.unassignedOnly?.length ?? 0) > 0 || undefined,
		},
		{ enabled: !isGateLoading && featureAvailable && !!hasViewPermission }
	);

	if (isGateLoading) {
		return null;
	}

	if (!featureAvailable) {
		return <Navigate to="/feature-disabled/" replace />;
	}

	if (!hasViewPermission) {
		return (
			<div className="p-6">
				<div className="bg-py-primary-1 flex w-[400px] gap-1.5 rounded-sm p-5">
					<FontAwesomeIcon icon={faInfoCircle} className="text-py-primary-7 text-lg" />
					<div className="font-semibold">Missing required permissions.</div>
				</div>
			</div>
		);
	}

	const workflows = data?.workflows ?? [];
	const totalCount = data?.totalCount ?? 0;
	const pageCount = Math.ceil(totalCount / PAGE_SIZE);

	const hasActiveFilters = Object.keys(filters).length > 0 || !!debouncedSearchValue;

	const actionButtons = [
		<ButtonStandard
			key="launch-workflow"
			data-testid="launchWorkflowButton"
			color="primary"
			iconLeft={faPlus}
			dangerouslySetClassName="w-auto"
			onClick={() => navigate(appendCurrentLocationAsFrom("/launch-workflow/"))}
		>
			Launch Workflow
		</ButtonStandard>,
	];

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={setDebouncedSearch}
			onFilterButtonPress={() => setIsFiltersOpen(!isFiltersOpen)}
			activeFiltersCount={Object.keys(filters).length}
			searchPlaceholder="Search Workflows..."
		/>
	);

	const toolbarInformationRow = <ReadableWorkflowsFilterChips filters={filters} setFilters={setFilters} />;

	const drawer = (
		<WorkflowsFilterPanel
			isOpen={isFiltersOpen}
			onClose={() => setIsFiltersOpen(false)}
			filters={filters}
			onApplyFilters={(newFilters) => setFilters(newFilters)}
		/>
	);

	const mainContent = (
		<div>
			<AllWorkflowsTable
				workflows={workflows}
				isLoading={isLoading}
				onSortChange={setSort}
				activeSort={sanitizeSortBy(sort.name)}
				activeSortDirection={sanitizeSortDirection(sort.direction)}
				emptyStateText={hasActiveFilters ? "No workflows match the applied filters" : "No Workflows Found"}
			/>
			{pageCount > 1 && (
				<div data-testid="paginationContainer" className="mt-4 flex justify-center">
					<Pagination currentPage={pageNumber} pageCount={pageCount} onPageChange={setPageNumber} />
				</div>
			)}
		</div>
	);

	return (
		<InfoPageTemplate
			actionButtons={actionButtons}
			toolbarActionRow={toolbarActionRow}
			toolbarInformationRow={toolbarInformationRow}
			drawer={drawer}
			mainContent={mainContent}
			pageTitle={PAGE_TITLE}
			pageHeading={isNewNav ? <PageHeading>{PAGE_TITLE}</PageHeading> : undefined}
			navigationLinks={WORKFLOWS_NAVIGATION_LINKS}
			activeNavigationLinkKey="all-workflows"
		/>
	);
};

export default AllWorkflows;
